/*
 * better-sell — anonymous collection re-rank (see docs/GA4-CONVSCORE-SPEC.md).
 *
 * On a collection page it reads the product ids the theme already stamps on each
 * grid tile (`li[data-product-id]`), asks the app for the conversion-ranked
 * order (GET — cached by the browser and the app), and reorders the tiles IN
 * PLACE — product slots only, so interleaved promo tiles keep their positions.
 * Global order (same for everyone), safe for logged-out and logged-in alike.
 *
 * Also:
 *  - tracks a CLICK (surface=collection, source=conversion_rank) when a shopper
 *    clicks a re-ranked tile, via the app proxy — feeds engine attribution.
 *  - re-runs after the theme's AJAX filter re-render replaces the grid
 *    (MutationObserver, disconnected during our own mutations so we never
 *    react to ourselves).
 *
 * Coexistence with theme JS (verified against Kuwait-v6):
 *  - back/forward navigation: skipped entirely, so main.js scroll-restore can
 *    anchor to the product the shopper left from.
 *  - custom-pagination load-more: the LAST product tile is pinned in place
 *    (its element is the theme's insertion anchor for the next page), and we
 *    stop re-ranking once the tile count grows (load-more pages stay native).
 *
 * DEPLOY: ship via the theme-push skill + theme-reviewer — never edit the live
 * theme directly. Include once on the collection template:
 *     <script src="{{ 'bs-collection-rank.js' | asset_url }}" defer="defer"></script>
 *
 * Verified against Kuwait-v6 markup:
 *   grid  = <ul class="grid ...">           (parent of the tiles)
 *   tile  = <li class="js-pagination-result" data-product-id="{{ product.id }}">
 *   promo = <li class="js-pagination-result ...">   (no data-product-id — left in place)
 */
(function () {
  "use strict";

  var PROXY_BASE = "/apps/better-sell/api";
  var MIN_TILES = 2; // nothing to reorder below this
  var MAX_IDS = 100; // cap request size on long/infinite grids
  var rerankedIds = {}; // productId -> true, for click attribution
  var running = false;
  var observer = null;
  var initialTileCount = 0;

  // Only real collection pages: /collections/<handle>, but NOT a product page
  // (/collections/<handle>/products/<handle>) and not /collections index.
  function isCollectionPage() {
    var p = location.pathname;
    return /\/collections\/[^/]+\/?$/.test(p) && p.indexOf("/products/") === -1;
  }

  // Back/forward navigation: the theme's scroll-restore (main.js) anchors the
  // viewport to a remembered product — reordering underneath it would land the
  // shopper on the wrong products. Skip the feature for restored views.
  function isBackForwardNav() {
    try {
      var nav = performance.getEntriesByType("navigation")[0];
      return nav && nav.type === "back_forward";
    } catch (e) {
      return false;
    }
  }

  // ...but main.js restores on ANY navigation type when it has a fresh saved
  // state (reload, or link-back to the same collection within 2 minutes) —
  // mirror its getPageKey()/getBackupState()/MAX_AGE_MS and stand down then too.
  function hasFreshScrollRestoreState() {
    try {
      var path = window.location.pathname + window.location.search;
      var hash = 0;
      for (var i = 0; i < path.length; i++) {
        hash = ((hash << 5) - hash) + path.charCodeAt(i);
        hash = hash & hash;
      }
      var raw = localStorage.getItem("sr_" + "p" + Math.abs(hash).toString(36));
      if (!raw) return false;
      var state = JSON.parse(raw);
      return Boolean(state && state.t && Date.now() - state.t < 2 * 60 * 1000);
    } catch (e) {
      return false;
    }
  }

  function tiles() {
    return Array.prototype.slice.call(
      document.querySelectorAll("li[data-product-id]")
    );
  }

  /** Reorder product tiles inside their grid. Returns true if the DOM changed. */
  function reorder(grid, current, order) {
    // Single-parent safety: replaceChild would throw mid-loop (stranding tiles
    // as comment placeholders) if any tile lives outside this grid.
    for (var i = 0; i < current.length; i++) {
      if (current[i].parentElement !== grid) return false;
    }

    var byId = {};
    current.forEach(function (t) {
      byId[t.getAttribute("data-product-id")] = t;
    });

    // Pin the LAST product tile: the theme's load-more (custom-pagination.js)
    // inserts the next page after the last tile it captured at init — moving it
    // would splice page 2+ into the middle of the grid.
    var pinned = current[current.length - 1];

    var seen = {};
    var newTiles = [];
    order.forEach(function (id) {
      var el = byId[String(id)];
      if (el && el !== pinned && !seen[id]) {
        seen[id] = 1;
        newTiles.push(el);
      }
    });
    // Any tile the server didn't rank keeps its relative order after the ranked
    // ones (except the pinned tile, which stays last).
    current.forEach(function (t) {
      var id = t.getAttribute("data-product-id");
      if (!seen[id] && t !== pinned) newTiles.push(t);
    });
    newTiles.push(pinned);
    if (newTiles.length !== current.length) return false; // never partial-render

    // Already in the right order? Don't touch the DOM (this also breaks any
    // possible observer feedback loop at its root).
    var same = true;
    for (var j = 0; j < current.length; j++) {
      if (current[j] !== newTiles[j]) {
        same = false;
        break;
      }
    }
    if (same) return false;

    // Swap each product slot for a placeholder, then fill the slots in the new
    // order. Promo <li>s (no data-product-id) are untouched, keeping their slots.
    var holders = current.map(function (t) {
      var c = document.createComment("bs");
      grid.replaceChild(c, t);
      return c;
    });
    newTiles.forEach(function (t, k) {
      grid.replaceChild(t, holders[k]);
    });
    return true;
  }

  // CLICK attribution: fires once per tile click on a re-ranked grid. Goes via
  // the app proxy (same pattern as the home-recommendations widget), which
  // appends logged_in_customer_id server-side for signed identity.
  function onGridClick(ev) {
    var tile = ev.target && ev.target.closest && ev.target.closest("li[data-product-id]");
    if (!tile) return;
    var id = tile.getAttribute("data-product-id");
    if (!id || !rerankedIds[id]) return;
    try {
      var payload = JSON.stringify({
        eventType: "CLICK",
        productId: "gid://shopify/Product/" + id,
        surface: "collection",
        source: "conversion_rank",
      });
      var blob = new Blob([payload], { type: "application/json" });
      // sendBeacon survives the navigation the click is about to cause.
      if (!(navigator.sendBeacon && navigator.sendBeacon(PROXY_BASE + "/track", blob))) {
        fetch(PROXY_BASE + "/track", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: payload,
          keepalive: true,
          credentials: "same-origin",
        }).catch(function () {});
      }
    } catch (e) {
      /* tracking must never break the storefront */
    }
  }

  function run() {
    if (running) return;
    var current = tiles();
    if (current.length < MIN_TILES) return;
    // Load-more grew the grid — stop re-ranking (appended pages stay native;
    // re-shuffling them would fight the theme's insertion logic).
    if (initialTileCount && current.length > initialTileCount) return;
    var grid = current[0].parentElement;
    if (!grid) return;

    var ids = current
      .map(function (t) {
        return t.getAttribute("data-product-id");
      })
      .slice(0, MAX_IDS);
    running = true;

    fetch(PROXY_BASE + "/collection-rank?ids=" + ids.join(","), {
      credentials: "same-origin",
    })
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (data) {
        if (!data || !Array.isArray(data.order) || data.order.length === 0) return;
        // Re-query in case the theme re-rendered the grid meanwhile.
        var live = tiles();
        if (live.length < MIN_TILES || !live[0].parentElement) return;
        // Never react to our own DOM mutations: pause the observer, flush any
        // queued records, mutate, then resume.
        if (observer) observer.disconnect();
        try {
          reorder(live[0].parentElement, live, data.order);
        } finally {
          if (observer) {
            observer.takeRecords();
            observeGrid();
          }
        }
        data.order.forEach(function (id) {
          rerankedIds[id] = true;
        });
      })
      .catch(function () {
        /* leave the native order on any failure */
      })
      .then(function () {
        running = false;
      });
  }

  function observeGrid() {
    var first = tiles()[0];
    if (!first || !first.parentElement) return; // nothing to watch (empty grid)
    var watchRoot = first.parentElement.parentElement || first.parentElement;
    observer.observe(watchRoot, { childList: true, subtree: true });
  }

  function boot() {
    if (!isCollectionPage() || isBackForwardNav() || hasFreshScrollRestoreState()) return;
    var initial = tiles();
    if (initial.length < MIN_TILES) return; // empty grid: stay inert, no observer
    initialTileCount = initial.length;

    document.addEventListener("click", onGridClick, true);

    // AJAX filters re-render the tiles in native order — watch the grid's
    // ancestor and re-rank when product tiles are REPLACED (a changed first
    // tile). Our own mutations never reach this: the observer is disconnected
    // while we reorder, and an unchanged order doesn't mutate at all.
    var debounce = null;
    observer = new MutationObserver(function (muts) {
      var relevant = muts.some(function (m) {
        return Array.prototype.some.call(m.addedNodes, function (n) {
          return (
            n.nodeType === 1 &&
            ((n.matches && n.matches("li[data-product-id]")) ||
              (n.querySelector && n.querySelector("li[data-product-id]")))
          );
        });
      });
      if (!relevant) return;
      clearTimeout(debounce);
      debounce = setTimeout(run, 150); // let the theme finish its own DOM work
    });
    observeGrid();

    run();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  // NOTE: reorders in place with no hide-then-reveal, to protect LCP; one
  // reflow is possible when the (cached) response returns.
})();
